import { Autocomplete, Button, Grid, TextField } from "@mui/material";
import GlobalDialogContent from "../../GlobalDialogContent";
import { useFormik } from "formik";
import * as Yup from 'yup';
import { useDispatch, useSelector } from "react-redux";
import { showSnackbar } from "@/redux/reducer/snackbarSlice";
import { closeDialog } from "@/redux/reducer/dialogSlice";
import { setModuleRefresh } from "@/redux/reducer/registration/RefreshSlice";
import { Send } from "@mui/icons-material";
import { useEffect, useState } from "react";
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { ThemeCreateService, ThemeUpdateService } from "@/utils/services/roles.permission.service";
import { SewaCategoryListService } from "@/utils/services/sewa-master.service";

const AddThemes = () => {
    const payload = useSelector((state: any) => state.dialog?.payload);
    const dispatch = useDispatch();
    const [sewaList, setSewaList] = useState<any[]>([]);

    const formik = useFormik({
        initialValues: {
            category_id: payload?.category_id || '',
            name: payload?.name || '',
            description: payload?.description || '',
            icon: payload?.icon || '',
        },
        validationSchema: Yup.object({
            category_id: Yup.string().required('Sewa kshetra is Required'),
            name: Yup.string().required('Theme Name is Required'),
            description: Yup.string().required('Description is Required'),
            icon: Yup.mixed()
                .required('Icon is required')
                .test(
                    'fileType',
                    'Only image files are allowed',
                    (value) => {
                        if (!value) return false;
                        return value instanceof File && value.type.startsWith('image/');
                    }
                ),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            const formData = new FormData();
            formData.append("category_id", values.category_id);
            formData.append("name", values.name);
            formData.append("description", values.description);

            if (values?.icon instanceof File) {
                formData.append("icon", values.icon);
            }

            if (payload?.id) {
                formData.append("id", payload.id);
            }

            const apiFunction = payload?.id ? ThemeUpdateService : ThemeCreateService;
            const { code, message } = await apiFunction(formData);
            if (code === 200) {
                dispatch(showSnackbar({ type: "success", message: message || 'Form Submitted Successfully.' }));
                dispatch(closeDialog());
                dispatch(setModuleRefresh({ moduleName: 'ThemeRefresh', refresh: true }));
            } else {
                dispatch(showSnackbar({ type: "error", message: message || 'Something Went Wrong!' }));
            }
            setSubmitting(false);
        }
    });

    useEffect(() => {
        fetchSewaCategory();
    }, []);

    const fetchSewaCategory = async () => {
        const { code, data } = await SewaCategoryListService({});
        if (code === 200 && data?.sewa_categories) {
            setSewaList(data?.sewa_categories);
        } else {
            setSewaList([]);
        }
    };

    const handlePhotoChange = (e: any) => {
        const file = e.target.files?.[0];
        if (!file) return;
        formik.setFieldValue("icon", file);
        e.target.value = "";
    };

    const handleRemovePhoto = () => {
        formik.setFieldValue("icon", '');
    };

    const iconPreviewSrc = formik.values.icon
        ? formik.values.icon instanceof File
            ? URL.createObjectURL(formik.values.icon)
            : `${process.env.REACT_APP_CDN_URL}/${formik.values.icon.document_path}`
        : null;

    return (
        <form onSubmit={formik.handleSubmit}>
            <GlobalDialogContent
                dialogBody={
                    <Grid container spacing={2}>
                        <Grid item xs={12} display="flex" flexDirection="column" alignItems="center" mb={1}>
                            <div style={{ position: "relative", width: 100, height: 100, }} >
                                <div
                                    style={{
                                        width: 100,
                                        height: 100,
                                        borderRadius: "50%",
                                        overflow: "hidden",
                                        border: "1px solid #ffcdcd",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: "#fafafa",
                                    }}
                                >
                                    {iconPreviewSrc ? (
                                        <img
                                            src={iconPreviewSrc}
                                            alt="icon"
                                            width={100}
                                            height={100}
                                            style={{ objectFit: "cover", borderRadius: "50%" }}
                                        />
                                    ) : (
                                        <Button component="label" sx={{ minWidth: 0, width: "100%", height: "100%", borderRadius: "50%" }}>
                                            <AddAPhotoIcon color="disabled" />
                                            <input
                                                type="file"
                                                hidden
                                                onChange={handlePhotoChange}
                                                accept="image/*"
                                            />
                                        </Button>
                                    )}
                                </div>

                                {iconPreviewSrc && (
                                    <HighlightOffIcon
                                        onClick={handleRemovePhoto}
                                        style={{
                                            position: "absolute",
                                            top: -4,
                                            right: -4,
                                            cursor: "pointer",
                                            borderRadius: "50%",
                                            background: "#fff",
                                            fontSize: 20,
                                        }}
                                        color="error"
                                    />
                                )}

                                {iconPreviewSrc && (
                                    <Button
                                        component="label"
                                        size="small"
                                        sx={{
                                            position: "absolute",
                                            bottom: -6,
                                            right: -6,
                                            minWidth: 0,
                                            width: 28,
                                            height: 28,
                                            borderRadius: "20%",
                                            background: "#fff",
                                            boxShadow: 1,
                                            p: 0,
                                        }}
                                    >
                                        <AddAPhotoIcon fontSize="small" />
                                        <input
                                            type="file"
                                            hidden
                                            onChange={handlePhotoChange}
                                            accept="image/*"
                                        />
                                    </Button>
                                )}
                            </div>

                            {formik.touched.icon && formik.errors.icon && (
                                <div style={{ color: "#d32f2f", fontSize: 12, marginTop: 4 }}>
                                    {formik.errors.icon as string}
                                </div>
                            )}
                        </Grid>

                        <Grid item xs={6}>
                            <Autocomplete
                                options={sewaList}
                                getOptionLabel={(option) => option?.category_name || ""}
                                value={sewaList.find(
                                    (item) => item.id === formik.values.category_id
                                ) || null}
                                onChange={(e, value) => {
                                    formik.setFieldValue('category_id', value?.id || '');
                                }}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                disableClearable
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        label="Select Sewa Kshetra *"
                                        error={formik.touched.category_id && Boolean(formik.errors.category_id)}
                                        helperText={Boolean(formik.touched.category_id && formik.errors.category_id)}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Name of Theme *"
                                name="name"
                                size="small"
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                placeholder="Enter Theme Name"
                                error={formik.touched.name && Boolean(formik.errors.name)}
                                helperText={Boolean(formik.touched.name && formik.errors.name)}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description*"
                                name="description"
                                size="small"
                                value={formik.values.description}
                                onChange={formik.handleChange}
                                placeholder="Enter Description"
                                multiline
                                rows={2}
                                error={formik.touched.description && Boolean(formik.errors.description)}
                                helperText={Boolean(formik.touched.description && formik.errors.description)}
                            />
                        </Grid>
                    </Grid>
                }
                dialogFooter={
                    <Button type="submit" disabled={formik.isSubmitting || !formik.isValid} variant="contained" color="primary" startIcon={<Send />}>
                        {formik.isSubmitting ? payload?.id ? "Updating..." : "Adding..." : payload?.id ? "Update Sewa Dal" : "Add Sewa Dal"}
                    </Button>
                }
            />
        </form>
    );
};

export default AddThemes;